import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosconfig from "../../Service/axiosConfig.js";
import cartApi from "../../Service/cartApi.js";

// ── Module-Level Debounce & Pending Quantities Tracking ─────────────
const pendingDebounceTimers = new Map();
const pendingTargetQuantities = new Map();
const pendingProducts = new Map();

/**
 * Executes server sync for a product once debouncing settles
 */
const executeServerSync = async (productId, dispatch, getState) => {
  const targetQty = pendingTargetQuantities.get(productId);
  if (targetQty === undefined) return;

  const productMeta = pendingProducts.get(productId);

  try {
    if (targetQty <= 0) {
      await dispatch(removeProductThunk({ productId, quantity: 1, targetQty: 0 }));
    } else {
      try {
        await dispatch(updateProductThunk({ productId, quantity: targetQty }));
      } catch (err) {
        if (productMeta) {
          await dispatch(addProductThunk({ product: productMeta, quantity: targetQty }));
        }
      }
    }
  } catch (error) {
    console.error(`Failed to sync product ${productId} to server:`, error);
  } finally {
    // Only remove pending record if no newer click or timer arrived while in-flight
    if (!pendingDebounceTimers.has(productId) && pendingTargetQuantities.get(productId) === targetQty) {
      pendingTargetQuantities.delete(productId);
      pendingProducts.delete(productId);
    }
  }
};

/**
 * Schedules debounced sync (350ms)
 */
const scheduleDebouncedSync = (productId, dispatch, getState) => {
  if (pendingDebounceTimers.has(productId)) {
    clearTimeout(pendingDebounceTimers.get(productId));
  }

  const timer = setTimeout(async () => {
    pendingDebounceTimers.delete(productId);
    await executeServerSync(productId, dispatch, getState);
  }, 350);

  pendingDebounceTimers.set(productId, timer);
};

/**
 * Flushes all pending debounce timers immediately.
 * Call before navigating to checkout, placing orders, or refreshing state.
 */
export const flushCartPendingMutations = async (dispatch, getState) => {
  const productIds = Array.from(pendingDebounceTimers.keys());
  for (const pid of productIds) {
    clearTimeout(pendingDebounceTimers.get(pid));
    pendingDebounceTimers.delete(pid);
  }
  const syncPromises = productIds.map((pid) => executeServerSync(pid, dispatch, getState));
  await Promise.all(syncPromises);
};

// ── Smart Non-Destructive Server Product Merge ──────────────────────
const mergeServerProducts = (state, serverProducts, authoritativeTotal) => {
  if (!serverProducts || !Array.isArray(serverProducts)) return;

  const serverMap = new Map(serverProducts.map((p) => [p.id, p]));
  const existingIds = new Set(state.products.map((p) => p.id));
  const serverIds = new Set(serverProducts.map((p) => p.id));
  const allIds = new Set([...existingIds, ...serverIds]);

  const merged = [];
  let hasPending = false;

  allIds.forEach((id) => {
    const isPending = pendingTargetQuantities.has(id) || pendingDebounceTimers.has(id);
    const localItem = state.products.find((p) => p.id === id);
    const serverItem = serverMap.get(id);

    if (isPending) {
      hasPending = true;
      const targetQty = pendingTargetQuantities.get(id);
      const currentLocalQty = targetQty !== undefined ? targetQty : localItem?.quantity;
      if (currentLocalQty > 0 && (serverItem || localItem)) {
        merged.push({
          ...(serverItem || localItem),
          quantity: currentLocalQty,
        });
      }
      // If currentLocalQty <= 0, item was removed optimistically by user, so omit from cart
    } else {
      if (serverItem && serverItem.quantity > 0) {
        merged.push(serverItem);
      }
    }
  });

  state.products = merged;

  if (!hasPending && typeof authoritativeTotal === "number" && !isNaN(authoritativeTotal)) {
    state.total = authoritativeTotal;
  } else {
    state.total = merged.reduce((acc, item) => {
      const basePrice = item.offer_price > 0 ? item.offer_price : item.price;
      const totalPrice = parseFloat(basePrice) + (isNaN(item.variant_Price) ? 0 : Number(item.variant_Price));
      return acc + (Number(item.quantity) || 1) * totalPrice;
    }, 0);
  }

  try {
    localStorage.setItem("state", JSON.stringify(state.products));
    localStorage.setItem("total", state.total);
    if (state.cartId) {
      localStorage.setItem("orderId", state.cartId);
    }
  } catch (e) {}
};

// ── Async Thunks (Live Server-Side Cart Mutations) ───────────────────

export const fetchCartThunk = createAsyncThunk(
  "cart/fetchCart",
  async (_, { rejectWithValue }) => {
    try {
      const normalized = await cartApi.fetchCart();
      return normalized;
    } catch (error) {
      console.warn("fetchCart failed on server:", error);
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const addProductThunk = createAsyncThunk(
  "cart/addProduct",
  async ({ product, quantity = 1 }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState().cart;
      const currentVersion = state.version;
      const productId = product.id || product.productId;
      const res = await cartApi.addItem(productId, quantity, currentVersion);
      return res;
    } catch (error) {
      // Re-fetch current server cart on any failure (409 conflict sync or non-409 rollback)
      dispatch(fetchCartThunk());
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateProductThunk = createAsyncThunk(
  "cart/updateProduct",
  async ({ productId, quantity }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState().cart;
      const currentVersion = state.version;
      const res = await cartApi.updateItem(productId, quantity, currentVersion);
      return res;
    } catch (error) {
      // Re-fetch current server cart on any failure (409 conflict sync or non-409 rollback)
      dispatch(fetchCartThunk());
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const removeProductThunk = createAsyncThunk(
  "cart/removeProduct",
  async ({ productId, quantity = 1, targetQty }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState().cart;
      const currentVersion = state.version;

      let resolvedQty = targetQty;
      if (resolvedQty === undefined) {
        const existing = state.products.find((p) => p.id === productId);
        resolvedQty = existing ? existing.quantity - quantity : 0;
      }

      // If decrementing quantity, check if item should be updated or removed
      if (resolvedQty > 0) {
        const res = await cartApi.updateItem(productId, resolvedQty, currentVersion);
        return res;
      }

      const res = await cartApi.removeItem(productId, currentVersion);
      return res;
    } catch (error) {
      // Re-fetch current server cart on any failure (409 conflict sync or non-409 rollback)
      dispatch(fetchCartThunk());
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// ── Initial State ─────────────────────────────────────────────

const getInitialLocalCart = () => {
  try {
    const raw = localStorage.getItem("state");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const getInitialLocalTotal = () => {
  try {
    const raw = localStorage.getItem("total");
    return raw ? parseFloat(raw) : 0;
  } catch {
    return 0;
  }
};

const initialState = {
  cartId: null,
  version: 1,
  products: getInitialLocalCart(),
  total: getInitialLocalTotal(),
  loading: false,
  conflict: false,
  conflictMessage: null,
  wishlists: [],
};

// ── Conflict Resolution Helper ────────────────────────────────

const handleConflict = (state, action) => {
  const payload = action.payload || action.error;
  const isConflict =
    payload?.code === "CART_VERSION_CONFLICT" ||
    payload?.isCartVersionConflict ||
    action.error?.message?.includes("Cart version conflict");

  const currentVersion =
    payload?.currentVersion ||
    payload?.error?.current_version ||
    payload?.freshCart?.version;

  if (currentVersion) {
    state.version = currentVersion;
  }

  const fresh = payload?.freshCart || payload?.latestCart;

  if (isConflict && fresh && fresh.products && fresh.products.length > 0) {
    state.cartId = fresh.cartId || state.cartId;
    state.products = fresh.products;
    state.total = fresh.total;
    state.conflict = true;
    state.conflictMessage =
      payload?.message ||
      "Cart was updated in another session or window. Your cart has been synchronized.";
    try {
      localStorage.setItem("state", JSON.stringify(fresh.products));
      localStorage.setItem("total", fresh.total);
    } catch (e) {}
  }
};

// ── Slice Definition ──────────────────────────────────────────

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setProducts: (state, action) => {
      state.products = action.payload;
      try {
        localStorage.setItem("state", JSON.stringify(state.products));
      } catch (e) {}
    },
    setTotal: (state, action) => {
      state.total = action.payload;
      try {
        localStorage.setItem("total", state.total);
      } catch (e) {}
    },
    // Synchronous optimistic reducers
    optimisticAddProduct: (state, action) => {
      const { product, quantity = 1 } = action.payload;
      const existingProduct = state.products.find((p) => p.id === product.id);
      if (existingProduct) {
        existingProduct.quantity += quantity;
      } else {
        state.products.push({ ...product, quantity });
      }
      const basePrice = product.offer_price > 0 ? product.offer_price : product.price;
      const totalPrice = parseFloat(basePrice) + (isNaN(product.variant_Price) ? 0 : Number(product.variant_Price));
      state.total += quantity * totalPrice;
      try {
        localStorage.setItem("total", state.total);
        localStorage.setItem("state", JSON.stringify(state.products));
      } catch (e) {}
    },
    optimisticRemoveProduct: (state, action) => {
      const { productId, quantity = 1 } = action.payload;
      const existingProduct = state.products.find((p) => p.id === productId);

      if (existingProduct) {
        if (existingProduct.quantity > quantity) {
          existingProduct.quantity -= quantity;
          const basePrice = existingProduct.offer_price > 0 ? existingProduct.offer_price : existingProduct.price;
          const totalPrice = parseFloat(basePrice) + (isNaN(existingProduct.variant_Price) ? 0 : Number(existingProduct.variant_Price));
          state.total = Math.max(0, state.total - quantity * totalPrice);
        } else {
          state.products = state.products.filter((p) => p.id !== productId);
          const basePrice = existingProduct.offer_price > 0 ? existingProduct.offer_price : existingProduct.price;
          const totalPrice = parseFloat(basePrice) + (isNaN(existingProduct.variant_Price) ? 0 : Number(existingProduct.variant_Price));
          state.total = Math.max(0, state.total - existingProduct.quantity * totalPrice);
        }
        try {
          localStorage.setItem("total", state.total);
          localStorage.setItem("state", JSON.stringify(state.products));
        } catch (e) {}
      }
    },
    optimisticUpdateProduct: (state, action) => {
      const { productId, quantity } = action.payload;
      const existingProduct = state.products.find((p) => p.id === productId);
      if (existingProduct) {
        const diff = quantity - existingProduct.quantity;
        existingProduct.quantity = quantity;
        const basePrice = existingProduct.offer_price > 0 ? existingProduct.offer_price : existingProduct.price;
        const totalPrice = parseFloat(basePrice) + (isNaN(existingProduct.variant_Price) ? 0 : Number(existingProduct.variant_Price));
        state.total = Math.max(0, state.total + diff * totalPrice);
        try {
          localStorage.setItem("total", state.total);
          localStorage.setItem("state", JSON.stringify(state.products));
        } catch (e) {}
      }
    },
    clearCart: (state) => {
      pendingDebounceTimers.forEach((timer) => clearTimeout(timer));
      pendingDebounceTimers.clear();
      pendingTargetQuantities.clear();
      pendingProducts.clear();
      state.products = [];
      state.total = 0;
      state.cartId = null;
      state.conflict = false;
      state.conflictMessage = null;
      try {
        localStorage.removeItem("state");
        localStorage.removeItem("total");
        localStorage.removeItem("orderId");
      } catch (e) {}
    },
    resetConflict: (state) => {
      state.conflict = false;
      state.conflictMessage = null;
    },
    addWishlist: (state, action) => {
      const { productId, wish_id } = action.payload;
      const existingProduct = state.wishlists.find((p) => p.id === productId);
      if (!existingProduct) {
        state.wishlists.push({ id: productId, wish_id });
      }
    },
    removeWishlist: (state, action) => {
      const { productId } = action.payload;
      state.wishlists = state.wishlists.filter((p) => p.id !== productId);
    },
    clearWishlist: (state) => {
      state.wishlists = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCartThunk
      .addCase(fetchCartThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCartThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.cartId = action.payload.cartId;
          state.version = action.payload.version || state.version || 1;
          state.conflict = false;
          mergeServerProducts(state, action.payload.products, action.payload.total);
        }
      })
      .addCase(fetchCartThunk.rejected, (state, action) => {
        state.loading = false;
        handleConflict(state, action);
      })
      // addProductThunk
      .addCase(addProductThunk.fulfilled, (state, action) => {
        if (action.payload) {
          state.cartId = action.payload.cartId;
          state.version = action.payload.version || state.version || 1;
          state.conflict = false;
          mergeServerProducts(state, action.payload.products, action.payload.total);
        }
      })
      .addCase(addProductThunk.rejected, (state, action) => {
        handleConflict(state, action);
      })
      // updateProductThunk
      .addCase(updateProductThunk.fulfilled, (state, action) => {
        if (action.payload) {
          state.cartId = action.payload.cartId;
          state.version = action.payload.version || state.version || 1;
          state.conflict = false;
          mergeServerProducts(state, action.payload.products, action.payload.total);
        }
      })
      .addCase(updateProductThunk.rejected, (state, action) => {
        handleConflict(state, action);
      })
      // removeProductThunk
      .addCase(removeProductThunk.fulfilled, (state, action) => {
        if (action.payload) {
          state.cartId = action.payload.cartId;
          state.version = action.payload.version || state.version || 1;
          state.conflict = false;
          mergeServerProducts(state, action.payload.products, action.payload.total);
        }
      })
      .addCase(removeProductThunk.rejected, (state, action) => {
        handleConflict(state, action);
      });
  },
});

// Selectors
export const selectCartProducts = (state) => state.cart.products;
export const selectCartTotal = (state) => state.cart.total;
export const selectCartVersion = (state) => state.cart.version;
export const selectCartConflict = (state) => state.cart.conflict;
export const selectCartConflictMessage = (state) => state.cart.conflictMessage;
export const selectWishlists = (state) => state.cart.wishlists;

// Export raw actions
export const {
  clearCart,
  setProducts,
  setTotal,
  resetConflict,
  addWishlist,
  removeWishlist,
  clearWishlist,
  optimisticAddProduct,
  optimisticRemoveProduct,
  optimisticUpdateProduct,
} = cartSlice.actions;

/**
 * addProduct:
 * Instant optimistic UI update + debounced server persistence (350ms).
 * Eliminates intermediate server network storms, rubberbanding, and 409 conflicts.
 * 100% backward compatible with existing UI component calls.
 */
export const addProduct = ({ product, quantity = 1 }) => async (dispatch, getState) => {
  const productId = product.id || product.productId;

  // 1. Instant optimistic update in Redux
  dispatch(cartSlice.actions.optimisticAddProduct({ product, quantity }));

  // 2. Determine target quantity from updated state
  const state = getState().cart;
  const existing = state.products.find((p) => p.id === productId);
  const targetQty = existing ? existing.quantity : quantity;

  // 3. Track pending target quantity & metadata
  pendingTargetQuantities.set(productId, targetQty);
  if (!pendingProducts.has(productId)) {
    pendingProducts.set(productId, product);
  }

  // 4. Schedule debounced server sync (350ms)
  scheduleDebouncedSync(productId, dispatch, getState);
};

/**
 * removeProduct:
 * Instant optimistic UI update + debounced server persistence (350ms).
 * Eliminates intermediate server network storms, rubberbanding, and 409 conflicts.
 * 100% backward compatible with existing UI component calls.
 */
export const removeProduct = ({ productId, quantity = 1 }) => async (dispatch, getState) => {
  const state = getState().cart;
  const existing = state?.products?.find((p) => p.id === productId);
  const targetQty = existing ? Math.max(0, existing.quantity - quantity) : 0;

  // 1. Instant optimistic update in Redux
  dispatch(cartSlice.actions.optimisticRemoveProduct({ productId, quantity }));

  // 2. Track pending target quantity
  pendingTargetQuantities.set(productId, targetQty);

  // 3. Schedule debounced server sync (350ms)
  scheduleDebouncedSync(productId, dispatch, getState);
};

/**
 * updateProduct:
 * Instant optimistic UI update + debounced server persistence (350ms).
 */
export const updateProduct = ({ productId, quantity }) => async (dispatch, getState) => {
  // 1. Instant optimistic update in Redux
  dispatch(cartSlice.actions.optimisticUpdateProduct({ productId, quantity }));

  // 2. Track pending target quantity
  pendingTargetQuantities.set(productId, quantity);

  // 3. Schedule debounced server sync (350ms)
  scheduleDebouncedSync(productId, dispatch, getState);
};

/**
 * fetchProducts:
 * Active server cart synchronizer with non-destructive merge.
 */
export const fetchProducts = () => async (dispatch) => {
  return dispatch(fetchCartThunk());
};

export default cartSlice.reducer;
